import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { membershipOrganizationsService } from '../../services/membershipOrganizations';
import type {
  MembershipOrganization,
  MembershipOrganizationPayload,
} from '../../types/membership';

interface MembershipOrganizationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  /** Null when creating. */
  organization: MembershipOrganization | null;
  onSaved: (organization: MembershipOrganization) => void;
}

const emptyForm: MembershipOrganizationPayload = {
  name: '',
  expiry_day: null,
  expiry_month: null,
  global_cards: false,
};

/** Empty string when unset, so the number inputs stay controlled. */
function toInput(value: number | null): string {
  return value === null ? '' : String(value);
}

export default function MembershipOrganizationFormDialog({
  open,
  onOpenChange,
  gymSlug,
  organization,
  onSaved,
}: MembershipOrganizationFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<MembershipOrganizationPayload>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(
      organization
        ? {
            name: organization.name,
            expiry_day: organization.expiry_day,
            expiry_month: organization.expiry_month,
            global_cards: organization.global_cards,
          }
        : emptyForm
    );
  }, [open, organization]);

  // Half a renewal date is not one: the backend refuses it, and so does the
  // save button, rather than letting staff find out after typing.
  const expiryComplete = (form.expiry_day === null) === (form.expiry_month === null);
  const canSave = form.name.trim().length > 0 && expiryComplete;

  const setNumber = (field: 'expiry_day' | 'expiry_month', raw: string) => {
    const parsed = raw === '' ? null : Number(raw);
    setForm((prev) => ({ ...prev, [field]: Number.isNaN(parsed) ? null : parsed }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || saving) return;

    setSaving(true);
    try {
      const saved = organization
        ? await membershipOrganizationsService.update(gymSlug, organization.id, form)
        : await membershipOrganizationsService.create(gymSlug, form);

      onSaved(saved);
    } catch (error) {
      toast({
        title: organization
          ? t('gymAdmin.membershipOrganizations.updateError')
          : t('gymAdmin.membershipOrganizations.createError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {organization
              ? t('gymAdmin.membershipOrganizations.editTitle')
              : t('gymAdmin.membershipOrganizations.newTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('gymAdmin.membershipOrganizations.formDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="organization-name">
              {t('gymAdmin.membershipOrganizations.fields.name')}
            </Label>
            <Input
              id="organization-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization-expiry-day">
              {t('gymAdmin.membershipOrganizations.fields.expiry')}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="organization-expiry-day"
                type="number"
                min={1}
                max={31}
                className="w-20"
                placeholder={t('gymAdmin.membershipOrganizations.fields.day')}
                value={toInput(form.expiry_day)}
                onChange={(e) => setNumber('expiry_day', e.target.value)}
              />
              <span className="text-muted-foreground">/</span>
              <Input
                id="organization-expiry-month"
                type="number"
                min={1}
                max={12}
                className="w-20"
                placeholder={t('gymAdmin.membershipOrganizations.fields.month')}
                value={toInput(form.expiry_month)}
                onChange={(e) => setNumber('expiry_month', e.target.value)}
                aria-label={t('gymAdmin.membershipOrganizations.fields.month')}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('gymAdmin.membershipOrganizations.fields.expiryHint')}
            </p>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="organization-global-cards">
                {t('gymAdmin.membershipOrganizations.fields.globalCards')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('gymAdmin.membershipOrganizations.fields.globalCardsHint')}
              </p>
            </div>
            <Switch
              id="organization-global-cards"
              checked={form.global_cards}
              onCheckedChange={(checked) =>
                setForm((prev) => ({ ...prev, global_cards: checked }))
              }
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave || saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
